import React from 'react';
import {
  BellRing,
  CircleCheck,
  CircleDot,
  CircleX,
  FileCheck2,
  FileSignature,
  MailWarning,
  Send,
  Stethoscope,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';

export type OdrWorkflowPresentation = {
  value: string;
  label: string;
  icon: LucideIcon;
  className: string;
};

export const ODR_WORKFLOW_PRESENTATIONS: OdrWorkflowPresentation[] = [
  { value: 'received', label: 'Charges ready for review', icon: CircleDot, className: '[--odr-workflow-color:color-mix(in_srgb,var(--semantic-warning)_70%,var(--neutral-gray-120))]' },
  { value: 'ready_to_assign_clinician', label: 'Ready to assign clinician', icon: UserPlus, className: '[--odr-workflow-color:var(--azure-primary)]' },
  { value: 'clinician_assigned', label: 'Clinician notification pending', icon: MailWarning, className: '[--odr-workflow-color:color-mix(in_srgb,var(--semantic-warning)_35%,var(--semantic-error))]' },
  { value: 'assigned_to_clinician', label: 'Waiting for clinician review', icon: Stethoscope, className: '[--odr-workflow-color:color-mix(in_srgb,var(--azure-primary)_55%,var(--semantic-error))]' },
  { value: 'clinician_review_complete', label: 'Ready for affidavit setup', icon: FileSignature, className: '[--odr-workflow-color:color-mix(in_srgb,var(--azure-primary)_50%,var(--semantic-success))]' },
  { value: 'participant_created', label: 'Ready to prepare affidavit', icon: FileCheck2, className: '[--odr-workflow-color:color-mix(in_srgb,var(--semantic-success)_70%,var(--semantic-warning))]' },
  { value: 'affidavit_generated', label: 'Affidavit ready to send', icon: Send, className: '[--odr-workflow-color:color-mix(in_srgb,var(--azure-primary)_25%,var(--semantic-error))]' },
  { value: 'affidavit_sent', label: 'Affidavit sent', icon: CircleCheck, className: '[--odr-workflow-color:var(--semantic-success)]' },
  { value: 'closed', label: 'Closed', icon: CircleX, className: '[--odr-workflow-color:var(--neutral-gray-80)]' },
];

const UNKNOWN_PRESENTATION: OdrWorkflowPresentation = {
  value: 'unknown',
  label: 'Workflow status unavailable',
  icon: BellRing,
  className: '[--odr-workflow-color:var(--neutral-gray-100)]',
};

export const getOdrWorkflowPresentation = (stage: unknown): OdrWorkflowPresentation => {
  const normalized = String(stage ?? '').trim();
  return ODR_WORKFLOW_PRESENTATIONS.find((item) => item.value === normalized) || UNKNOWN_PRESENTATION;
};

export const OdrWorkflowStatusIcon = ({
  stage,
  className,
  showTitle = true,
}: {
  stage: unknown;
  className?: string;
  showTitle?: boolean;
}) => {
  const presentation = getOdrWorkflowPresentation(stage);
  const Icon = presentation.icon;
  return (
    <span
      role="img"
      aria-label={`Status: ${presentation.label}`}
      title={showTitle ? presentation.label : undefined}
      className={cn('inline-flex items-center justify-center text-[color:var(--odr-workflow-color)]', presentation.className, className)}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
  );
};
